import { useCallback } from 'react';

export interface ComputeRule {
  sourceFields: string[];
  operator: 'add' | 'subtract' | 'multiply' | 'divide';
  targetField: string;
  precision?: number;
}

export function useComputeRulesV2(rules: ReadonlyArray<ComputeRule>) {
  const applyComputeRules = useCallback(
    (rowData: Record<string, unknown>, changedField: string): Record<string, unknown> => {
      if (rules.length === 0) return rowData;

      const updatedRow = { ...rowData };
      let changed = false;

      for (const rule of rules) {
        if (!rule.sourceFields.includes(changedField)) continue;

        const values = rule.sourceFields.map((f) => {
          const val = updatedRow[f];
          return val != null ? Number(val) : 0;
        });

        if (values.some((v) => Number.isNaN(v))) continue;

        let result: number;
        switch (rule.operator) {
          case 'add':
            result = values.reduce((a, b) => a + b, 0);
            break;
          case 'subtract':
            result = values.reduce((a, b) => a - b);
            break;
          case 'multiply':
            result = values.reduce((a, b) => a * b, 1);
            break;
          case 'divide':
            result = values[1] !== 0 ? values[0] / values[1] : 0;
            break;
          default:
            continue;
        }

        const precision = rule.precision ?? 4;
        updatedRow[rule.targetField] = Number(result.toFixed(precision));
        changed = true;
      }

      return changed ? updatedRow : rowData;
    },
    [rules]
  );

  const getAffectedFields = useCallback(
    (changedField: string): string[] => {
      return rules.filter((r) => r.sourceFields.includes(changedField)).map((r) => r.targetField);
    },
    [rules]
  );

  return { applyComputeRules, getAffectedFields };
}
